import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ProdutosService } from '../../../../services/produtos/produto.service';
import { ApiService } from '../../../../services/api.service';
import { UnidadeMedidaDeItemService } from '../../../../services/configuracoes/unidade-de-medida.service';
import { GrupoService } from '../../../../services/grupo/grupo.service';
import { MarcaService } from '../../../../services/marca/marca.service';
import { PessoaService } from '../../../../services/pessoas/pessoas.service';
import { ProdutosDTO, ProdutosResponseDTO } from '../../../../models/produto.models';
import { TipoDeImposto } from '../../../../enums/tipoDeImposto.enum';
import { TipoDeDesconto } from '../../../../enums/TipoDeDesconto.enum';
import { SubGrupoService } from '../../../../services/grupo/sub-grupo.service';
import { EmpresaConfigService } from '../../../../services/configuracoes/empresa.service';
import { ImpressoraService } from '../../../../services/impressora/impressora.service';
import { MoedaDTO } from '../../../../models/configuracoes/empresa.model';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { TipoItem } from '../../../../enums/TipoItem.enum';
import { environment } from '../../../../environments/environment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AjusteEstoqueService } from '../../../../services/estoque/ajuste-estoque.service';
import { AjusteDeEstoqueDTO, TipoOperacaoDeEstoque, TipoContaEstoque } from '../../../../models/estoque/ajuste-estoque.model';
import { ProdutoVarianteModificadorService } from '../../../../services/produto-variante-modificador/produto-variante-modificador.service';
import {
  VarianteDePrecoDeItemDTO,
  GrupoDeModificadorDePrecoDeItemDTO,
  ModificadorDePrecoDeItemDTO,
} from '../../../../models/produto-variante-modificador/produto-variante-modificador.model';
import { toLocalISOString, fromLocalISOString } from '../../../../shared/utils/date-utils';
import { validarPromocaoNovidade } from '../../../../shared/utils/promocao-novidade.util';
import { extrairErros400, sanitizarMensagemErro } from '../../../../shared/utils/error-utils';
import { ImagemUtils } from '../../../../shared/utils/imagem-utils';

@Component({
  selector: 'app-produtos-edit',
  templateUrl: './produtos-edit.component.html',
  styleUrls: ['./produtos-edit.component.scss'],
  providers: [MessageService],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class ProdutosEditComponent implements OnInit, OnDestroy {
  env = environment;

  form: ProdutosDTO = this.createEmptyForm();
  idEdit?: number;
  isFormSubmitted = false;
  submitting = false;

  // ---- Promocao e Novidade (estado dos p-calendar fora do form: ProdutosDTO)
  promocao = false;
  promocaoAteEmData: Date | null = null;
  novidade = false;
  novidadeAteEmData: Date | null = null;
  /** Data minima dos calendarios — impede selecao no passado (reforco de RN02/RN04). */
  dataMinimaPromocaoNovidade: Date = new Date();
  saldoOriginal: number = 0;
  /** `true` quando o item em edicao e de sistema (imutavel/critico).
   *  Controla banner informativo e readonly/disabled dos campos criticos.
   *  Backend protege esses itens (ex: "Servicio de Envio"); preco/grupo/unidade
   *  nao podem ser alterados. */
  ehItemDeSistema = false;

  // Combos
  unidadesMedida: { label: string; value: number }[] = [];
  grupos: { label: string; value: number }[] = [];
  subGrupos: { label: string; value: number }[] = [];
  marcas: { label: string; value: number }[] = [];
  fornecedores: { label: string; value: number }[] = [];
  impressoras: { label: string; value: number }[] = [];
  habilitarMiniShopping: { label: string; value: boolean }[] = [];
  permiteImprimir: { label: string; value: boolean }[] = [];

  tiposDeDesconto = Object.values(TipoDeDesconto).map((value) => ({
    label: value,
    value,
  }));

  // Configurações da empresa
  habilitarMiniShoppingEmpresaConfig: boolean = false;
  imprimirItensDeAcordoImpressoraEmpresaConfig: boolean = false;
  /** Controla a exibição dos campos de impressão de PEDIDO.
   *  Verdadeiro quando a empresa imprime pedido automaticamente (local ou minishop). */
  imprimirAutomaticoPedidoEmpresaConfig: boolean = false;

  // Upload / preview
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;
  imagemOriginal: string | null = null;
  imagemAlterada = false;

  // Lightbox (expansao da imagem)
  imagemExpandida = false;

  // empresa/usuario
  idEmpresa: string = '';

  /** Flag para evitar limpar subgrupo durante carga do produto */
  private isLoadingProduto = false;

  // Variantes de preco
  variantes: VarianteDePrecoDeItemDTO[] = [];

  // Grupos de modificadores
  gruposModificadores: GrupoDeModificadorDePrecoDeItemDTO[] = [];

  // Dialog de confirmacao de remocao de grupo de modificador
  mostrarDialogRemoverGrupo = false;
  grupoParaRemover: { index: number; grupo: GrupoDeModificadorDePrecoDeItemDTO } | null = null;
  mensagemRemoverGrupo = '';

  // Auto-save: subjects de debounce e status
  private autoSaveVariante$ = new Subject<number>();
  private autoSaveGrupo$ = new Subject<number>();
  private autoSaveModificador$ = new Subject<{ grupoIndex: number; modIndex: number }>();
  private autoSaveSubscriptions: Subscription[] = [];

  // Mapa de status de salvamento: 'idle' | 'salvando' | 'salvo' | 'erro'
  varianteSaveStatus: Map<number, string> = new Map();
  grupoSaveStatus: Map<number, string> = new Map();
  modificadorSaveStatus: Map<string, string> = new Map();

  // Referências aos inputs para foco automatico ao adicionar
  @ViewChildren('varianteDescricaoInput') varianteDescricaoInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('grupoDescricaoInput') grupoDescricaoInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('modificadorDescricaoInput') modificadorDescricaoInputs!: QueryList<ElementRef<HTMLInputElement>>;

  tipoDeItensOptions: { value: string; label: string }[] = [];
  tipoDeImpostoOptions: { value: string; label: string }[] = [];

  moedaPadrao: MoedaDTO | undefined;

  // Mapa de id da unidade de medida → casas decimais (para campos de estoque)
  private unidadeMedidaCasasDecimaisMap: Map<number, number> = new Map();
  casasDecimaisEstoque: number = 0;

  constructor(
    private produtosService: ProdutosService,
    private apiService: ApiService,
    private unidadeMedidaService: UnidadeMedidaDeItemService,
    private grupoService: GrupoService,
    private subGrupoService: SubGrupoService,
    private marcaService: MarcaService,
    private pessoaService: PessoaService,
    private empresaConfigService: EmpresaConfigService,
    private impressoraService: ImpressoraService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private produtoVarianteModificadorService: ProdutoVarianteModificadorService,
    private ajusteEstoqueService: AjusteEstoqueService,
  ) { }

  private langChangeSub!: Subscription;

  ngOnInit(): void {
    this.idEmpresa = sessionStorage.getItem('id') || '';
    this.loadTranslate();
    this.loadUnidadesMedida();
    this.loadGrupos();
    this.loadSubGrupos();
    this.loadMarcas();
    this.loadFornecedores();
    this.loadEmpresaConfig();
    this.loadImpressoras();
    this.carregarMoedaPadrao();

    this.buildTipoDeItensOptions();
    this.buildTipoDeImpostoOptions();

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslate();
      this.buildTipoDeItensOptions();
      this.buildTipoDeImpostoOptions();
    });

    // Auto-save com debounce para variantes
    this.autoSaveSubscriptions.push(
      this.autoSaveVariante$.pipe(debounceTime(3000)).subscribe((index) => {
        this.salvarVariante(index);
      })
    );

    // Auto-save com debounce para grupos de modificadores
    this.autoSaveSubscriptions.push(
      this.autoSaveGrupo$.pipe(debounceTime(3000)).subscribe((index) => {
        this.salvarGrupoModificador(index);
      })
    );

    // Auto-save com debounce para modificadores individuais
    this.autoSaveSubscriptions.push(
      this.autoSaveModificador$.pipe(debounceTime(3000)).subscribe(({ grupoIndex, modIndex }) => {
        this.salvarModificador(grupoIndex, modIndex);
      })
    );

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.idEdit = +id;
      this.loadProduto(this.idEdit);
      this.loadVariantes(+id);
      this.loadGruposModificadores(+id);
    }
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
    this.autoSaveSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  get locale(): string {
    return this.moedaPadrao?.pais?.local?.replaceAll('_', '-') || 'es-PY';
  }

  get currencyCode(): string {
    return this.moedaPadrao?.codigoOperacao || 'PYG';
  }

  get currencySymbol(): string {
    const codigo = this.currencyCode.toUpperCase();
    switch (codigo) {
      case 'BRL': return 'R$';
      case 'USD': return '$';
      case 'PYG': return 'Gs';
      default: return 'Gs';
    }
  }

  get casasDecimaisDaMoeda(): number {
    return this.moedaPadrao?.casasDecimais ?? 0;
  }

  /** `true` quando o tipo de item selecionado e SERVICO.
   *  Mantido por ser utilizado nas validacoes de campos de estoque (custo,
   *  saldo, estoque minimo) — servicos nao exigem esses campos. */
  get ehServico(): boolean {
    return this.form?.tipoItem?.value === TipoItem.SERVICO;
  }

  /** `true` quando o preco de venda e invalido.
   *  Regra unica para qualquer tipo de item: invalido apenas se nulo/undefined.
   *  Zero e valores positivos sao validos para qualquer tipo. */
  get precoDeVendaInvalido(): boolean {
    return this.form?.precoDeVenda == null;
  }

  /**
   * Handler chamado ao focar o campo de preco de venda.
   *
   * Workaround para um problema do p-inputNumber em moedas sem casas decimais
   * (ex.: PYG): se o campo ja contem 0 e o usuario aperta a tecla 0, o
   * componente detecta que o valor nao mudou e descarta o keystroke. Como o
   * preco pode comecar em 0 (vindo do backend ou novo produto), o workaround
   * se aplica a qualquer tipo de item.
   *
   * Solucao: ao focar com valor 0, limpa para null. Assim o usuario digita 0
   * normalmente e, se sair sem digitar nada, o blur restaura o 0.
   */
  onPrecoDeVendaFocus(): void {
    if (this.ehItemDeSistema) return;
    if (this.form.precoDeVenda === 0) {
      this.form.precoDeVenda = null as any;
    }
  }

  /**
   * Handler chamado ao sair do campo de preco de venda.
   * Se o campo ficou vazio (usuario nao digitou nada), restaura para 0 —
   * preco zero e valido para qualquer tipo de item.
   */
  onPrecoDeVendaBlur(): void {
    if (this.ehItemDeSistema) return;
    if (this.form.precoDeVenda == null) {
      this.form.precoDeVenda = 0;
    }
  }

  formatarCampoPreco(event: any, campo: 'precoDeVenda' | 'custo'): void {
    const casasDecimais = this.moedaPadrao?.casasDecimais ?? 0;
    const valor = this.extrairValorNumerico(event.target.value, casasDecimais);
    (this.form as any)[campo] = valor;
    event.target.value = valor !== null ? String(valor) : '';
  }

  formatarCampoPrecoVariante(event: any, variante: VarianteDePrecoDeItemDTO): void {
    const casasDecimais = this.moedaPadrao?.casasDecimais ?? 0;
    const valor = this.extrairValorNumerico(event.target.value, casasDecimais);
    variante.precoDeVenda = valor ?? 0;
    event.target.value = valor !== null ? String(valor) : '';
  }

  formatarCampoPrecoModificador(event: any, mod: ModificadorDePrecoDeItemDTO): void {
    const casasDecimais = this.moedaPadrao?.casasDecimais ?? 0;
    const valor = this.extrairValorNumerico(event.target.value, casasDecimais);
    mod.precoDeVenda = valor ?? 0;
    event.target.value = valor !== null ? String(valor) : '';
  }

  private extrairValorNumerico(valor: string, casasDecimais: number): number | null {
    if (casasDecimais === 0) {
      const limpo = valor.replace(/[^\d-]/g, '');
      if (limpo === '' || limpo === '-') return null;
      return parseInt(limpo, 10);
    } else {
      const limpo = valor.replace(/[^\d.-]/g, '');
      if (limpo === '' || limpo === '-') return null;
      return parseFloat(limpo);
    }
  }

  carregarMoedaPadrao(): void {
    const idEmpresa = sessionStorage.getItem('id') || '';
    if (!idEmpresa) return;

    this.empresaConfigService.get(`/configuracaoEmpresa/${idEmpresa}`).subscribe({
      next: (res) => {
        this.moedaPadrao = res?.moeda;
      },
      error: (err) => {
        console.error('Erro ao buscar configuração da empresa:', err);
      },
    });
  }

  // ==========================
  // LOADERS
  // ==========================
  loadTranslate() {
    this.translate.get(['produtos.labels.sim', 'produtos.labels.nao']).subscribe((translations) => {
      this.habilitarMiniShopping = [
        { label: translations['produtos.labels.sim'], value: true },
        { label: translations['produtos.labels.nao'], value: false },
      ];
      this.permiteImprimir = [
        { label: translations['produtos.labels.sim'], value: true },
        { label: translations['produtos.labels.nao'], value: false },
      ];
    });
  }

  loadProduto(id: number): void {
    this.isLoadingProduto = true;
    this.produtosService.get(`/item/${id}`).subscribe((response: any) => {
      this.form = {
        id: response.id,
        codigoItem: response.codigoItem,
        codigoFabricante: response.codigoFabricante,
        barras: response.barras,
        nome: response.nome,
        marca_id: response.marca_id,
        unidadeMedidaDeItem_id: response.unidadeMedidaDeItem_id,
        precoDeVenda: response.precoDeVenda,
        tipoDeImposto: response.tipoDeImposto?.enum ?? response.tipoDeImposto,
        custo: response.custo,
        grupo_id: response.grupo_id,
        subGrupo_id: response.subGrupo_id,
        // Saldo e estoque minimo defaultam para 0 caso a API retorne
        // null/undefined — zero e valor valido para esses campos.
        estoqueMinimo: response.estoqueMinimo ?? 0,
        tipoDeDesconto: response.tipoDeDesconto?.enum ?? response.tipoDeDesconto,
        desconto: response.desconto,
        fornecedorPrincipal_id: response.fornecedorPrincipal_id,
        status: response.status,
        saldo: response.saldo ?? 0,
        imagem: response.imagem,
        descricao: response.descricao || '',
        tipoItem: this.mapTipoItemToOption(response.tipoItem),
        habilitarMiniShopping: response.venderNoMiniShop === true,
        impressoraPadraoId: response.impressoraPadraoId,
        impressoraPadraoPedidoId: response.impressoraPadraoPedidoId,
        permiteImprimir: response.permiteImprimir === true || response.permiteImprimir === 'true',
        permiteImprimirPedido: response.permiteImprimirPedido === true || response.permiteImprimirPedido === 'true',
        itemDeSistema: response.itemDeSistema === true,
      };

      this.ehItemDeSistema = response.itemDeSistema === true;

      // Promocao/Novidade — parse ISO local -> Date (simetrico a toLocalISOString).
      this.promocao = response.promocao === true;
      this.promocaoAteEmData = fromLocalISOString(response.promocaoAteEm);
      this.novidade = response.novidade === true;
      this.novidadeAteEmData = fromLocalISOString(response.novidadeAteEm);

      this.saldoOriginal = response.saldo ?? 0;
      this.imagemOriginal = response.imagem ?? null;
      this.imagemAlterada = false;

      // Atualizar casas decimais do estoque com base na unidade carregada
      if (this.form.unidadeMedidaDeItem_id) {
        this.atualizarCasasDecimaisEstoque(this.form.unidadeMedidaDeItem_id as number);
      }

      this.imagePreviewUrl = `${this.env.imgHost}/${this.idEmpresa}/item/${this.form.imagem}`;

      // Permite limpeza de subgrupo apos carga inicial
      setTimeout(() => { this.isLoadingProduto = false; });
    }, (err) => {
      this.isLoadingProduto = false;
      if (err?.status === 404) {
        this.showError(this.translate.instant('produtos-edit.mensagens.produto_nao_encontrado'));
        return;
      }
      this.showError(this.translate.instant('produtos-edit.mensagens.erro_carregar_produto'));
    });
  }

  loadUnidadesMedida(): void {
    this.unidadeMedidaService.get('/unidaDeMedidaDeItemEmpresa', { page: 0, size: 1000, sort: 'id,asc' }).subscribe({
      next: (response: any) => {
        this.unidadeMedidaCasasDecimaisMap.clear();
        this.unidadesMedida = (response.content || []).map((item: any) => {
          const id = item.unidadeMedidaDeItemEmpresa?.id ?? item.id ?? 0;
          const casas = item.unidadeMedidaDeItemEmpresa?.casasDecimais ?? item.casasDecimais ?? 0;
          this.unidadeMedidaCasasDecimaisMap.set(id, casas);
          return {
            label: item.unidadeMedidaDeItemEmpresa?.nome ?? item.nome,
            value: id,
          };
        });

        // Se produto ja esta carregado, atualizar casas decimais
        if (this.form.unidadeMedidaDeItem_id) {
          this.atualizarCasasDecimaisEstoque(this.form.unidadeMedidaDeItem_id as number);
        }

        // Produto novo: preencher com a unidade de medida padrão da configuração
        if (!this.idEdit) {
          this.preencherUnidadeMedidaPadrao();
        }
      },
      error: () => {
        // Unidades de medida são opcionais — falha silenciosa
        this.unidadesMedida = [];
      },
    });
  }

  /** Busca a unidade de medida padrão da configuração geral e pré-seleciona no formulário */
  private preencherUnidadeMedidaPadrao(): void {
    this.apiService.read<any>('/configuracaoGeral').subscribe({
      next: (response: any) => {
        const idPadrao = response?.content?.[0]?.unidadeMedidaPadraoId;
        if (idPadrao && this.unidadesMedida.some(u => u.value === idPadrao)) {
          this.form.unidadeMedidaDeItem_id = idPadrao;
          this.atualizarCasasDecimaisEstoque(idPadrao);
        }
      },
      error: () => {
        // Falha silenciosa — campo fica sem valor padrão
      },
    });
  }

  /** Handler chamado quando a unidade de medida muda no dropdown */
  onUnidadeMedidaChange(): void {
    const id = this.form.unidadeMedidaDeItem_id as number;
    this.atualizarCasasDecimaisEstoque(id ?? 0);
  }

  /** Atualiza casas decimais de estoque com base na unidade selecionada e reformata valores */
  private atualizarCasasDecimaisEstoque(unidadeId: number): void {
    const novasCasas = this.unidadeMedidaCasasDecimaisMap.get(unidadeId) ?? 0;
    const casasAnteriores = this.casasDecimaisEstoque;
    this.casasDecimaisEstoque = novasCasas;

    // Reformatar valores existentes se casas decimais diminuiu
    if (novasCasas < casasAnteriores) {
      if (this.form.saldo != null) {
        this.form.saldo = this.arredondarParaCasas(this.form.saldo, novasCasas);
      }
      if (this.form.estoqueMinimo != null) {
        this.form.estoqueMinimo = this.arredondarParaCasas(this.form.estoqueMinimo, novasCasas);
      }
    }
  }

  /** Arredonda um valor para o numero de casas decimais especificado */
  private arredondarParaCasas(valor: number, casas: number): number {
    const fator = Math.pow(10, casas);
    return Math.round(valor * fator) / fator;
  }

  loadGrupos(): void {
    this.grupoService.get('/grupo').subscribe({
      next: (response) => {
        this.grupos = response.content.map((grupo) => ({
          label: grupo.nome,
          value: grupo.id ?? 0,
        }));
      },
      error: () => {
        // Grupos são opcionais — falha silenciosa
        this.grupos = [];
      },
    });
  }

  loadSubGrupos(): void {
    this.subGrupoService.get('/subgrupo').subscribe({
      next: (response) => {
        this.subGrupos = response.content.map((subGrupo) => ({
          label: subGrupo.nome,
          value: subGrupo.id ?? 0,
        }));
      },
      error: () => {
        // Subgrupos são opcionais — falha silenciosa
        this.subGrupos = [];
      },
    });
  }

  loadMarcas(): void {
    this.marcaService.get('/marca').subscribe({
      next: (response) => {
        this.marcas = response.content.map((marca) => ({
          label: marca.nome,
          value: marca.id ?? 0,
        }));
      },
      error: () => {
        // Marcas são opcionais — falha silenciosa
        this.marcas = [];
      },
    });
  }

  loadFornecedores(): void {
    this.pessoaService.get('/pessoas/buscar?categoriaFornecedor=true').subscribe({
      next: (response) => {
        this.fornecedores = (response?.content || []).map((fornecedor) => ({
          label: fornecedor.nome,
          value: fornecedor.id ?? 0,
        }));
      },
      error: () => {
        // Ausência de fornecedores é estado válido — não exibir toast de erro
        this.fornecedores = [];
      },
    });
  }

  loadEmpresaConfig(): void {
    this.empresaConfigService.getById('/configuracaoEmpresa/sessao').subscribe({
      next: (empresa: any) => {
        this.habilitarMiniShoppingEmpresaConfig = empresa.habilitarMiniShopping || false;
        this.imprimirItensDeAcordoImpressoraEmpresaConfig = empresa.imprimirItensDeAcordoImpressora || false;
        this.imprimirAutomaticoPedidoEmpresaConfig =
          empresa.imprimirAutomaticoPedidoLocal === true || empresa.imprimirAutomaticoPedidoMinishop === true;
      },
      error: (err) => console.error('Erro ao carregar configuração da empresa:', err),
    });
  }

  loadImpressoras(): void {
    this.impressoraService.get('/impressora').subscribe({
      next: (response: any) => {
        const impressoras = Array.isArray(response) ? response : (response.content || []);
        this.impressoras = impressoras.map((imp: any) => ({
          label: imp.nome,
          value: imp.codigoImpressora ?? 0,
        }));
      },
      error: () => {
        // Impressoras são opcionais — falha silenciosa
        this.impressoras = [];
      },
    });
  }

  // ==========================
  // VARIANTES DE PRECO
  // ==========================

  loadVariantes(itemId: number): void {
    this.produtoVarianteModificadorService.getVariantes(itemId).subscribe({
      next: (variantes) => {
        this.variantes = variantes || [];
      },
      error: (err) => {
        if (err?.status === 404) {
          this.variantes = [];
          return;
        }
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_carregar_variantes'));
      },
    });
  }

  adicionarVariante(): void {
    if (!this.idEdit) return;
    this.variantes.push({
      item_id: this.idEdit,
      descricao: '',
      precoDeVenda: 0,
    });
    // Foco automatico no input da nova variante apos renderizacao
    setTimeout(() => {
      const inputs = this.varianteDescricaoInputs?.toArray();
      if (inputs?.length) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
    });
  }

  removerVariante(index: number): void {
    const variante = this.variantes[index];
    if (variante.id) {
      this.produtoVarianteModificadorService.deleteVariante(variante.id).subscribe({
        next: () => {
          this.variantes.splice(index, 1);
        },
        error: () => {
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_variante'));
        },
      });
    } else {
      this.variantes.splice(index, 1);
    }
  }

  /** Dispara auto-save com debounce para uma variante */
  onVarianteChange(index: number): void {
    this.varianteSaveStatus.set(index, 'pendente');
    this.autoSaveVariante$.next(index);
  }

  salvarVariante(index: number): void {
    if (!this.idEdit) return;
    const variante = this.variantes[index];
    if (!variante.descricao?.trim()) return;

    this.varianteSaveStatus.set(index, 'salvando');

    if (variante.id) {
      this.produtoVarianteModificadorService.updateVariante(variante.id, variante).subscribe({
        next: (atualizada) => {
          // Merge propriedades no objeto existente para preservar referencia e manter foco
          Object.assign(variante, atualizada);
          this.setStatusTemporario(this.varianteSaveStatus, index, 'salvo');
        },
        error: () => {
          this.varianteSaveStatus.set(index, 'erro');
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_variante'));
        },
      });
    } else {
      this.produtoVarianteModificadorService.createVariante({ ...variante, item_id: this.idEdit }).subscribe({
        next: (criada) => {
          // Merge propriedades no objeto existente para preservar referencia e manter foco
          Object.assign(variante, criada);
          this.setStatusTemporario(this.varianteSaveStatus, index, 'salvo');
        },
        error: () => {
          this.varianteSaveStatus.set(index, 'erro');
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_variante'));
        },
      });
    }
  }

  // ==========================
  // GRUPOS DE MODIFICADORES
  // ==========================

  loadGruposModificadores(itemId: number): void {
    this.produtoVarianteModificadorService.getGrupos(itemId).subscribe({
      next: (grupos) => {
        this.gruposModificadores = grupos || [];
      },
      error: (err) => {
        if (err?.status === 404) {
          this.gruposModificadores = [];
          return;
        }
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_carregar_modificadores'));
      },
    });
  }

  adicionarGrupoModificador(): void {
    if (!this.idEdit) return;
    this.gruposModificadores.push({
      item_id: this.idEdit,
      descricao: '',
      maximoDeModificadores: 1,
      minimoDeModificadores: 0,
      modificadores: [],
    });
    // Foco automatico no input do novo grupo apos renderizacao
    setTimeout(() => {
      const inputs = this.grupoDescricaoInputs?.toArray();
      if (inputs?.length) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
    });
  }

  removerGrupoModificador(index: number): void {
    const grupo = this.gruposModificadores[index];

    // Se o grupo ainda nao foi salvo, remove direto sem confirmacao
    if (!grupo.id) {
      this.gruposModificadores.splice(index, 1);
      return;
    }

    const qtdOpcoes = grupo.modificadores.length;
    this.mensagemRemoverGrupo = qtdOpcoes > 0
      ? this.translate.instant('produtos-edit.mensagens.confirmar_remover_grupo_com_opcoes', { qtd: qtdOpcoes })
      : this.translate.instant('produtos-edit.mensagens.confirmar_remover_grupo');

    this.grupoParaRemover = { index, grupo };
    this.mostrarDialogRemoverGrupo = true;
  }

  cancelarRemocaoGrupo(): void {
    this.mostrarDialogRemoverGrupo = false;
    this.grupoParaRemover = null;
    this.mensagemRemoverGrupo = '';
  }

  confirmarRemocaoGrupoFinal(): void {
    if (this.grupoParaRemover) {
      this.executarRemocaoGrupo(this.grupoParaRemover.index, this.grupoParaRemover.grupo);
    }
    this.mostrarDialogRemoverGrupo = false;
    this.grupoParaRemover = null;
    this.mensagemRemoverGrupo = '';
  }

  private executarRemocaoGrupo(index: number, grupo: GrupoDeModificadorDePrecoDeItemDTO): void {
    const modificadoresComId = grupo.modificadores.filter(m => m.id);
    if (modificadoresComId.length > 0) {
      let deletados = 0;
      let erros = 0;
      modificadoresComId.forEach(mod => {
        this.produtoVarianteModificadorService.deleteModificador(mod.id!).subscribe({
          next: () => {
            deletados++;
            if (deletados + erros === modificadoresComId.length && erros === 0) {
              this.deletarGrupo(index, grupo.id!);
            }
          },
          error: () => {
            erros++;
            if (deletados + erros === modificadoresComId.length) {
              this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_grupo_modificador'));
            }
          },
        });
      });
    } else {
      this.deletarGrupo(index, grupo.id!);
    }
  }

  private deletarGrupo(index: number, grupoId: number): void {
    this.produtoVarianteModificadorService.deleteGrupo(grupoId).subscribe({
      next: () => {
        this.gruposModificadores.splice(index, 1);
      },
      error: () => {
        this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_grupo_modificador'));
      },
    });
  }

  /** Dispara auto-save com debounce para um grupo de modificador */
  onGrupoModificadorChange(grupoIndex: number): void {
    this.grupoSaveStatus.set(grupoIndex, 'pendente');
    this.autoSaveGrupo$.next(grupoIndex);
  }

  /** Toggle: "Cliente deve escolher?" → mapeia para minimoDeModificadores */
  onToggleObrigatorio(grupoIndex: number, obrigatorio: boolean): void {
    this.gruposModificadores[grupoIndex].minimoDeModificadores = obrigatorio ? 1 : 0;
    this.onGrupoModificadorChange(grupoIndex);
  }

  /** Toggle: "Pode escolher mais de um?" → mapeia para maximoDeModificadores */
  onToggleMultiplo(grupoIndex: number, multiplo: boolean): void {
    this.gruposModificadores[grupoIndex].maximoDeModificadores = multiplo ? 3 : 1;
    this.onGrupoModificadorChange(grupoIndex);
  }

  salvarGrupoModificador(grupoIndex: number): void {
    if (!this.idEdit) return;
    const grupo = this.gruposModificadores[grupoIndex];
    if (!grupo.descricao?.trim()) return;

    this.grupoSaveStatus.set(grupoIndex, 'salvando');

    if (grupo.id) {
      this.produtoVarianteModificadorService.updateGrupo(grupo.id, grupo).subscribe({
        next: (atualizado) => {
          // Merge propriedades no objeto existente para preservar referencia e manter foco
          Object.assign(grupo, { ...atualizado, modificadores: grupo.modificadores });
          this.setStatusTemporario(this.grupoSaveStatus, grupoIndex, 'salvo');
        },
        error: () => {
          this.grupoSaveStatus.set(grupoIndex, 'erro');
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_grupo_modificador'));
        },
      });
    } else {
      this.produtoVarianteModificadorService.createGrupo({ ...grupo, item_id: this.idEdit }).subscribe({
        next: (criado) => {
          // Merge propriedades no objeto existente para preservar referencia e manter foco
          Object.assign(grupo, { ...criado, modificadores: grupo.modificadores });
          this.setStatusTemporario(this.grupoSaveStatus, grupoIndex, 'salvo');
        },
        error: () => {
          this.grupoSaveStatus.set(grupoIndex, 'erro');
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_grupo_modificador'));
        },
      });
    }
  }

  adicionarModificador(grupoIndex: number): void {
    if (!this.idEdit) return;
    const grupo = this.gruposModificadores[grupoIndex];
    if (!grupo.id) return;
    grupo.modificadores.push({
      item_id: this.idEdit,
      grupoDeModificadorDePrecoDeItem_id: grupo.id,
      descricao: '',
      precoDeVenda: 0,
    });
    // Foco automatico no input do novo modificador apos renderizacao
    setTimeout(() => {
      const inputs = this.modificadorDescricaoInputs?.toArray();
      if (inputs?.length) {
        inputs[inputs.length - 1].nativeElement.focus();
      }
    });
  }

  removerModificador(grupoIndex: number, modIndex: number): void {
    const grupo = this.gruposModificadores[grupoIndex];
    const modificador = grupo.modificadores[modIndex];
    if (modificador.id) {
      this.produtoVarianteModificadorService.deleteModificador(modificador.id).subscribe({
        next: () => {
          grupo.modificadores.splice(modIndex, 1);
        },
        error: () => {
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_remover_modificador'));
        },
      });
    } else {
      grupo.modificadores.splice(modIndex, 1);
    }
  }

  /** Dispara auto-save com debounce para um modificador */
  onModificadorChange(grupoIndex: number, modIndex: number): void {
    const key = `${grupoIndex}-${modIndex}`;
    this.modificadorSaveStatus.set(key, 'pendente');
    this.autoSaveModificador$.next({ grupoIndex, modIndex });
  }

  salvarModificador(grupoIndex: number, modIndex: number): void {
    if (!this.idEdit) return;
    const grupo = this.gruposModificadores[grupoIndex];
    if (!grupo.id) return;
    const modificador = grupo.modificadores[modIndex];
    if (!modificador.descricao?.trim()) return;

    const key = `${grupoIndex}-${modIndex}`;
    this.modificadorSaveStatus.set(key, 'salvando');

    if (modificador.id) {
      this.produtoVarianteModificadorService.updateModificador(modificador.id, modificador).subscribe({
        next: (atualizado) => {
          // Merge propriedades no objeto existente para preservar referencia e manter foco
          Object.assign(modificador, atualizado);
          this.setStatusTemporarioStr(this.modificadorSaveStatus, key, 'salvo');
        },
        error: () => {
          this.modificadorSaveStatus.set(key, 'erro');
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_modificador'));
        },
      });
    } else {
      this.produtoVarianteModificadorService.createModificador({
        ...modificador,
        item_id: this.idEdit!,
        grupoDeModificadorDePrecoDeItem_id: grupo.id,
      }).subscribe({
        next: (criado) => {
          // Merge propriedades no objeto existente para preservar referencia e manter foco
          Object.assign(modificador, criado);
          this.setStatusTemporarioStr(this.modificadorSaveStatus, key, 'salvo');
        },
        error: () => {
          this.modificadorSaveStatus.set(key, 'erro');
          this.showError(this.translate.instant('produtos-edit.mensagens.erro_salvar_modificador'));
        },
      });
    }
  }

  private buildTipoDeItensOptions() {
    this.tipoDeItensOptions = Object.values(TipoItem).map(filtro => ({
      value: filtro,
      label: this.translate.instant(`tipo_de_item.${filtro}`)
    }));
  }

  /** Converte o tipoItem vindo do backend para o formato do dropdown */
  private mapTipoItemToOption(tipoItem: any): any {
    if (!tipoItem) return this.tipoDeItensOptions.find(o => o.value === TipoItem.MERCADORIA) || null;
    const enumValue = typeof tipoItem === 'string' ? tipoItem : tipoItem?.enum;
    return this.tipoDeItensOptions.find(o => o.value === enumValue) || null;
  }

  private buildTipoDeImpostoOptions() {
    this.tipoDeImpostoOptions = Object.values(TipoDeImposto).map(filtro => ({
      value: filtro,
      label: this.translate.instant(`tipo_de_imposto.${filtro}`)
    }));
  }

  getTipoItemLabel(): string {
    return this.form.tipoItem?.label || this.translate.instant('produtos.placeholders.selecionar_tipo_produto');
  }

  getTipoImpostoLabel(): string {
    return this.tipoDeImpostoOptions.find(o => o.value === this.form.tipoDeImposto)?.label || this.translate.instant('produtos.placeholders.selecionar_tipo_imposto');
  }

  // ==========================
  // LIGHTBOX (Expansao da imagem)
  // ==========================
  abrirImagemExpandida(): void {
    if (this.imagePreviewUrl) {
      this.imagemExpandida = true;
    }
  }

  fecharImagemExpandida(): void {
    this.imagemExpandida = false;
  }

  // Atalhos de teclado
  @HostListener('document:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    // Ctrl+Enter: atualizar produto
    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.submit();
      return;
    }

    // Escape: fechar dialog de remocao, lightbox ou voltar para lista
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (this.mostrarDialogRemoverGrupo) {
        this.cancelarRemocaoGrupo();
      } else if (this.imagemExpandida) {
        this.fecharImagemExpandida();
      } else {
        this.navigateListaDeProdutos();
      }
      return;
    }

    // Enter: confirmar remocao do grupo quando dialog esta aberto
    if (ev.key === 'Enter' && !ev.ctrlKey && this.mostrarDialogRemoverGrupo) {
      ev.preventDefault();
      this.confirmarRemocaoGrupoFinal();
      return;
    }
  }

  // ==========================
  // IMAGE UPLOAD
  // ==========================
  private readonly formatosImagemPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) return;

    if (!this.formatosImagemPermitidos.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.arquivo_invalido'),
        detail: this.translate.instant('produtos-edit.mensagens.formato_imagem_invalido'),
      });
      return;
    }

    this.fileName = file.name;
    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;
    this.form.imagem = this.imageBase64;
    this.imagemAlterada = true;
  }

  onClearImage() {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.form.imagem = '';
    this.imagemAlterada = true;
  }

  // ==========================
  // FORM E SUBMIT
  // ==========================
  private mapFormToApiPayload(form: ProdutosDTO): any {
    const miniShopAtivo = this.toBoolean(form.habilitarMiniShopping);
    return {
      id: this.getValueOrNull(form.id),
      codigoItem: this.getValueOrNull(form.codigoItem),
      codigoFabricante: this.getValueOrNull(form.codigoFabricante),
      nome: this.getValueOrNull(form.nome),
      barras: this.getValueOrNull(form.barras),

      grupo_id: this.getValueOrNull(form.grupo_id),
      subGrupo_id: this.getValueOrNull(form.subGrupo_id),
      marca_id: this.getValueOrNull(form.marca_id),
      fornecedorPrincipal_id: this.getValueOrNull(form.fornecedorPrincipal_id),
      unidadeMedidaDeItem_id: this.getValueOrNull(form.unidadeMedidaDeItem_id),

      tipoDeImposto: this.getStringValue(form.tipoDeImposto),
      tipoDeDesconto: this.getStringValue(form.tipoDeDesconto),
      tipoItem: this.getStringValue(form.tipoItem?.value),

      status: this.toBoolean(form.status),
      venderNoMiniShop: this.toBoolean(form.habilitarMiniShopping),
      permiteImprimir: this.toBoolean(form.permiteImprimir),
      permiteImprimirPedido: this.toBoolean(form.permiteImprimirPedido),
      descricao: form.descricao || null,

      precoDeVenda: this.toNumber(form.precoDeVenda),
      custo: this.toNumber(form.custo),
      desconto: this.toNumber(form.desconto),
      estoqueMinimo: this.toNumber(form.estoqueMinimo),
      impressoraPadraoId: this.getValueOrNull(form.impressoraPadraoId),
      impressoraPadraoPedidoId: this.getValueOrNull(form.impressoraPadraoPedidoId),

      // Promocao/Novidade: so e enviada quando o MiniShop esta ativo (secao visivel).
      // Com MiniShop desligado, zera tudo para nao gravar promocao/novidade "fantasma".
      // Data serializada como ISO local apenas quando a flag esta ligada (RN05).
      // Item de sistema sempre false/null (RN08), pois os campos ficam ocultos.
      promocao: miniShopAtivo && this.promocao,
      promocaoAteEm: miniShopAtivo && this.promocao && this.promocaoAteEmData ? toLocalISOString(this.promocaoAteEmData) : null,
      novidade: miniShopAtivo && this.novidade,
      novidadeAteEm: miniShopAtivo && this.novidade && this.novidadeAteEmData ? toLocalISOString(this.novidadeAteEmData) : null,
    };
  }

  submit(): void {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.idEdit) return;

    // Para item de sistema (ex: "Servicio de Envio"), grupo/unidade nao se
    // aplicam — o backend preserva nome/tipo/preco/custo mesmo que venham
    // vazios. Valida apenas o que faz sentido.
    const faltaGrupo = !this.ehItemDeSistema && !this.form.grupo_id;
    const unidadeValida = this.ehItemDeSistema
      || (this.form.unidadeMedidaDeItem_id != null && this.form.unidadeMedidaDeItem_id !== 0);

    const ehServico = this.ehServico;

    // Preco de venda e valido com 0 ou positivo para qualquer tipo de item.
    // Invalido apenas se nao foi preenchido (null/undefined).
    const precoInvalido = this.precoDeVendaInvalido;

    const camposFaltantes: string[] = [];
    if (!this.form.nome) camposFaltantes.push(this.translate.instant('produtos.labels.nome'));
    if (faltaGrupo) camposFaltantes.push(this.translate.instant('produtos.labels.grupo'));
    if (!this.form.tipoItem) camposFaltantes.push(this.translate.instant('produtos.labels.tipo_de_item'));
    if (!this.form.tipoDeImposto) camposFaltantes.push(this.translate.instant('produtos.labels.tipo_de_imposto'));
    if (!unidadeValida) camposFaltantes.push(this.translate.instant('produtos.labels.unidade_de_medida'));
    if (precoInvalido) camposFaltantes.push(this.translate.instant('produtos.labels.preco_de_venda'));
    if (!ehServico && this.form.custo == null) camposFaltantes.push(this.translate.instant('produtos.labels.custo'));
    // Saldo e estoque minimo: invalidos apenas se nao foram preenchidos
    // (null/undefined). Zero e valor valido — ex.: produto sem estoque inicial
    // ou que zerou.
    if (!ehServico && this.form.saldo == null) camposFaltantes.push(this.translate.instant('produtos.labels.saldo'));
    if (!ehServico && this.form.estoqueMinimo == null) camposFaltantes.push(this.translate.instant('produtos.labels.estoque_minimo'));

    if (camposFaltantes.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('produtos.mensagens.campos_obrigatorios_titulo'),
        detail: this.translate.instant('produtos.mensagens.campos_obrigatorios_prefixo') + camposFaltantes.join(', '),
        life: 6000,
      });
      return;
    }

    // Validacao client-side de promocao/novidade (RN01-RN04). So exigida quando a
    // secao esta visivel (MiniShop ativo e nao item de sistema); caso contrario
    // os campos estao ocultos/zerados e passam direto.
    if (this.form.habilitarMiniShopping && !this.ehItemDeSistema && !this.validarPromocaoNovidadeOuAvisar()) {
      return;
    }

    const payload = this.mapFormToApiPayload(this.form);
    const saldoAtual = this.toNumber(this.form.saldo);

    this.submitting = true;
    this.produtosService.put(`/item/${this.idEdit}`, payload).subscribe({
      next: () => {
        // Atualizar imagem separadamente se mudou (mesmo endpoint do mobile)
        if (this.imagemAlterada) {
          this.atualizarImagem(() => this.finalizarEdicao(saldoAtual));
        } else {
          this.finalizarEdicao(saldoAtual);
        }
      },
      error: (err) => {
        this.submitting = false;
        this.tratarErroSalvar(err);
      },
    });
  }

  /**
   * Valida promocao/novidade e, se houver erros, exibe-os via MessageService.
   * @returns `true` quando esta valido (pode prosseguir com o submit).
   */
  private validarPromocaoNovidadeOuAvisar(): boolean {
    const validacao = validarPromocaoNovidade(
      this.promocao,
      this.promocaoAteEmData,
      this.novidade,
      this.novidadeAteEmData,
    );
    if (!validacao.ok) {
      validacao.errosI18n.forEach((chave) => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('produtos.mensagens.campos_obrigatorios_titulo'),
          detail: this.translate.instant(chave),
          life: 6000,
        });
      });
      return false;
    }
    return true;
  }

  /** Exibe erro de salvamento — detalha array `erros` 400 (RN01-RN04) ou cai no generico. */
  private tratarErroSalvar(err: any): void {
    const mensagemPadrao = this.translate.instant('produtos-edit.mensagens.erro_atualizar_produto');
    const erros400 = extrairErros400(err, mensagemPadrao);
    if (err?.status === 400 && erros400.length > 0) {
      erros400.forEach((detail) => this.showError(detail));
      return;
    }
    this.showError(sanitizarMensagemErro(err?.error?.message, mensagemPadrao));
  }

  /**
   * Ao desligar o MiniShop, a secao de promocao/novidade fica oculta;
   * zera as flags e datas para nao gravar promocao/novidade "fantasma".
   */
  onMiniShopChange(): void {
    if (!this.form.habilitarMiniShopping) {
      this.promocao = false;
      this.promocaoAteEmData = null;
      this.novidade = false;
      this.novidadeAteEmData = null;
    }
  }

  /** Ao desligar o switch de promocao, zera a data (evita "valor fantasma" — RN05). */
  onPromocaoChange(): void {
    if (!this.promocao) {
      this.promocaoAteEmData = null;
    }
  }

  /** Ao desligar o switch de novidade, zera a data (evita "valor fantasma" — RN05). */
  onNovidadeChange(): void {
    if (!this.novidade) {
      this.novidadeAteEmData = null;
    }
  }

  private atualizarImagem(callback: () => void): void {
    const imagemPayload = { imagem: this.form.imagem || '' } as any;
    this.produtosService.put(`/item/atualizarImagem/${this.idEdit}`, imagemPayload).subscribe({
      next: () => callback(),
      error: () => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('produtos-edit.mensagens.erro_atualizar_imagem'),
        });
        // Continua mesmo com erro na imagem - produto ja foi salvo
        callback();
      },
    });
  }

  private finalizarEdicao(saldoAtual: number): void {
    this.submitting = false;
    if (saldoAtual !== this.saldoOriginal) {
      this.criarAjusteEstoque(saldoAtual);
    } else {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('common.sucesso'),
        detail: this.translate.instant('produtos-edit.mensagens.produto_atualizado'),
      });
      this.router.navigate(['lista-de-produtos']);
    }
  }

  private criarAjusteEstoque(saldoAtual: number): void {
    const diferenca = Math.abs(saldoAtual - this.saldoOriginal);
    const tipo = saldoAtual > this.saldoOriginal
      ? TipoOperacaoDeEstoque.ENTRADA
      : TipoOperacaoDeEstoque.SAIDA;

    const dto: AjusteDeEstoqueDTO = {
      tipoOperacaoDeEstoque: tipo,
      item_id: this.idEdit,
      quantidade: diferenca,
      emissao: new Date().toISOString().replace('Z', ''),
      tipoContaEstoque: TipoContaEstoque.NORMAL,
    };

    this.ajusteEstoqueService.post('/ajusteDeEstoque', dto).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('produtos-edit.mensagens.produto_atualizado'),
        });
        this.router.navigate(['lista-de-produtos']);
      },
      error: () => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('produtos-edit.mensagens.ajuste_estoque_erro'),
        });
        // Navega mesmo com erro - produto ja foi salvo
        this.router.navigate(['lista-de-produtos']);
      },
    });
  }

  // ==========================
  // HELPERS
  // ==========================
  private getValueOrNull(obj: any): number | null {
    return obj && typeof obj === 'object' && 'value' in obj ? obj.value : obj ?? null;
  }

  private getStringValue(obj: any): string | null {
    if (!obj || (typeof obj === 'string' && obj.trim() === '')) return null;
    return obj && typeof obj === 'object' && 'value' in obj ? obj.value : obj;
  }

  private toNumber(value: any): number {
    return value !== undefined && value !== null ? Number(value) : 0;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true';
  }

  /** Define status temporario que volta a 'idle' apos 2s */
  private setStatusTemporario(map: Map<number, string>, key: number, status: string): void {
    map.set(key, status);
    setTimeout(() => map.delete(key), 2000);
  }

  private setStatusTemporarioStr(map: Map<string, string>, key: string, status: string): void {
    map.set(key, status);
    setTimeout(() => map.delete(key), 2000);
  }

  private createEmptyForm(): ProdutosDTO {
    return {
      nome: '',
      barras: '',
      codigoFabricante: '',
      unidadeMedidaDeItem_id: 0,
      precoDeVenda: null as any,
      tipoDeImposto: 'IVA_10',
      custo: null as any,
      grupo_id: undefined,
      subGrupo_id: undefined,
      marca_id: undefined,
      // Saldo e estoque minimo iniciam em 0 — zero e valor valido (ex.:
      // produto recem-cadastrado sem entrada em estoque ainda).
      estoqueMinimo: 0,
      tipoDeDesconto: '',
      desconto: 0,
      fornecedorPrincipal_id: undefined,
      status: true,
      saldo: 0,
      imagem: '',
      descricao: '',
      tipoItem: undefined as any,
      habilitarMiniShopping: false,
      impressoraPadraoId: undefined,
      impressoraPadraoPedidoId: undefined,
      permiteImprimir: false,
      permiteImprimirPedido: false,
    };
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: this.translate.instant('common.erro'), detail });
  }

  /** Quando o grupo muda, limpa a seleção de subgrupo */
  onGrupoChange(grupoId: any): void {
    // Nao limpa durante carga do produto (edicao)
    if (this.isLoadingProduto) return;
    this.form.subGrupo_id = undefined;
  }

  // ==========================
  // TRACK BY (evita recriacao do DOM pelo Angular)
  // ==========================

  trackByVariante(index: number, variante: VarianteDePrecoDeItemDTO): any {
    return variante.id ?? `new-${index}`;
  }

  trackByGrupo(index: number, grupo: GrupoDeModificadorDePrecoDeItemDTO): any {
    return grupo.id ?? `new-${index}`;
  }

  trackByModificador(index: number, mod: ModificadorDePrecoDeItemDTO): any {
    return mod.id ?? `new-${index}`;
  }

  navigateListaDeProdutos() {
    this.router.navigate(['lista-de-produtos']);
  }
}